import React, { Suspense } from "react";
import {
	BrowserRouter as Router,
	Route,
	Routes,
	Outlet,
} from "react-router-dom";
import styled, { ThemeProvider } from "styled-components";
import theme from "./styles/theme";
import GlobalStyle from "./styles/globalStyles";
import { HelmetProvider } from "react-helmet-async";

// Main Components
import AnalyticsTracker from "./components/AnalyticsTracker";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Hero from "./components/Hero";
import Solutions from "./components/Solutions";
import PlatformStory from "./components/PlatformStory";
import CallToAction from "./components/CallToAction";

// Pages
import RequestDemo from "./components/RequestDemo";
import Pricing from "./components/Pricing";
import ContactUs from "./components/ContactUs";

// Redirects & Utility
import { PaymentCancel, PaymentSuccess } from "./components/PayRedirects";
import ScanRedirect from "./components/ScanRedirect";
import DineRouter from "./engine/DineRouter";
import CaterRouter from "./engine/CaterRouter";
import BarTabLanding from "./components/BarTabLanding";
import AboutUs from "./components/AboutUs";
import RestaurantLanding from "./components/RestaurantLanding";
import RestaurantReservationHub from "./components/RestaurantReservationHub";
import { ResourceArticle, ResourceHub } from "./components/RestaurantResources";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";

// --- Styled Components ---
const AppContainer = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 100vh;
`;

const MainContent = styled.main`
	flex-grow: 1;
	background-color: ${({ theme }) => theme.colors.background};
`;

const LoadingScreen = styled.div`
	height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: ${({ theme }) => theme.fonts.heading};
	color: ${({ theme }) => theme.colors.primary};
	font-size: 1.5rem;
	font-weight: 700;
	background-color: ${({ theme }) => theme.colors.background};
`;

const WebsiteLayout = () => {
	return (
		<AppContainer>
			<Header />
			<MainContent>
				<Outlet />
			</MainContent>
			<Footer />
		</AppContainer>
	);
};

const App = () => {
	return (
		<HelmetProvider>
			<ThemeProvider theme={theme}>
				<GlobalStyle />
				<Suspense fallback={<LoadingScreen>Loading Scerv...</LoadingScreen>}>
					<Router>
						<AnalyticsTracker />
						<Routes>
							{/* 1. MAIN WEBSITE */}
							<Route element={<WebsiteLayout />}>
								<Route
									path="/"
									element={
										<>
											<Hero />
											<PlatformStory />
											<Solutions />
											<CallToAction />
										</>
									}
								/>
								<Route path="/request-demo" element={<RequestDemo />} />
								<Route path="/contact" element={<ContactUs />} />
								<Route path="/pricing" element={<Pricing />} />
								<Route path="/about" element={<AboutUs />} />
								<Route path="/resources" element={<ResourceHub />} />
								<Route
									path="/resources/:slug"
									element={<ResourceArticle />}
								/>
								<Route path="/privacy-policy" element={<PrivacyPolicy />} />
								<Route path="/terms-of-service" element={<TermsOfService />} />
								<Route
									path="/r/:slug"
									element={<RestaurantLanding />}
								/>
								<Route
									path="/r/:slug/reserve"
									element={<RestaurantReservationHub />}
								/>
								<Route
									path="/restaurants/:slug"
									element={<RestaurantLanding />}
								/>
							</Route>

							{/* 2. APP REDIRECTS */}
							<Route path="/payment-success" element={<PaymentSuccess />} />
							<Route path="/payment-cancel" element={<PaymentCancel />} />
							<Route path="/scan" element={<ScanRedirect />} />
							<Route path="/dine/:token" element={<DineRouter />} />
							<Route path="/cater/:secret" element={<CaterRouter />} />
							<Route path="/bar/:restaurantId" element={<BarTabLanding />} />
						</Routes>
					</Router>
				</Suspense>
			</ThemeProvider>
		</HelmetProvider>
	);
};

export default App;
